import React, { useState } from "react";
import { CheckSquare, Square, X, User, AlertTriangle } from "lucide-react";
import { SupportTicket } from "@core/services/SupportTicketService";

interface TicketBulkActionsProps {
  selectedTickets: SupportTicket[];
  onClearSelection: () => void;
  onBulkStatusChange: (ticketIds: string[], status: string) => void;
  onBulkAssign: (ticketIds: string[], assignTo: string) => void;
  onBulkDelete: (ticketIds: string[]) => void;
}

export const TicketBulkActions: React.FC<TicketBulkActionsProps> = ({
  selectedTickets,
  onClearSelection,
  onBulkStatusChange,
  onBulkAssign,
  onBulkDelete,
}) => {
  const [showActions, setShowActions] = useState(false);
  const [bulkStatus, setBulkStatus] = useState("");
  const [bulkAssignTo, setBulkAssignTo] = useState("");

  const handleBulkStatusChange = () => {
    if (bulkStatus && selectedTickets.length > 0) {
      onBulkStatusChange(
        selectedTickets.map((t) => t.id),
        bulkStatus,
      );
      setBulkStatus("");
      setShowActions(false);
    }
  };

  const handleBulkAssign = () => {
    if (bulkAssignTo && selectedTickets.length > 0) {
      onBulkAssign(
        selectedTickets.map((t) => t.id),
        bulkAssignTo,
      );
      setBulkAssignTo("");
      setShowActions(false);
    }
  };

  const handleBulkDelete = () => {
    if (selectedTickets.length > 0) {
      const confirmed = confirm(
        `Êtes-vous sûr de vouloir supprimer ${selectedTickets.length} ticket${selectedTickets.length > 1 ? "s" : ""} ? Cette action est irréversible.`,
      );

      if (confirmed) {
        onBulkDelete(selectedTickets.map((t) => t.id));
        setShowActions(false);
      }
    }
  };

  if (selectedTickets.length === 0) {
    return null;
  }
  return (
    <div className="fixed bottom-6 left-6 bg-gray-800 border border-gray-600 rounded-lg shadow-lg p-4 z-40">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <CheckSquare className="w-4 h-4 text-purple-400" />
          <span className="text-sm font-medium text-white">
            {selectedTickets.length} ticket
            {selectedTickets.length > 1 ? "s" : ""} sélectionné
            {selectedTickets.length > 1 ? "s" : ""}
          </span>
        </div>
        <button
          onClick={onClearSelection}
          className="p-1 rounded hover:bg-gray-700 text-gray-400"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Actions */}
      <div className="flex gap-2 mb-3">
        <button
          onClick={() => setShowActions(!showActions)}
          className="px-3 py-1.5 bg-purple-500 text-white rounded-lg hover:bg-purple-600 text-sm font-medium"
        >
          Actions groupées
        </button>
        {/* Bouton de suppression directement visible */}
        <button
          onClick={handleBulkDelete}
          className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
        >
          Supprimer {selectedTickets.length} ticket
          {selectedTickets.length > 1 ? "s" : ""}
        </button>
      </div>

      {/* Action Panel */}
      {showActions && (
        <div className="space-y-3">
          {/* Status Change */}
          <div className="flex gap-2">
            <select
              value={bulkStatus}
              onChange={(e) => setBulkStatus(e.target.value)}
              className="px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm"
            >
              <option value="">Changer statut...</option>
              <option value="open">Ouvert</option>
              <option value="in_progress">En cours</option>
              <option value="resolved">Résolu</option>
              <option value="closed">Fermé</option>
            </select>
            <button
              onClick={handleBulkStatusChange}
              disabled={!bulkStatus}
              className="px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50 text-sm"
            >
              Appliquer
            </button>
          </div>

          {/* Assign */}
          <div className="flex gap-2">
            <select
              value={bulkAssignTo}
              onChange={(e) => setBulkAssignTo(e.target.value)}
              className="px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm"
            >
              <option value="">Assigner à...</option>
              <option value="0e425591-29a7-488e-b420-56a7295e1773">
                Julien Fritsch
              </option>
            </select>
            <button
              onClick={handleBulkAssign}
              disabled={!bulkAssignTo}
              className="px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 text-sm"
            >
              Assigner
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Hook pour gérer la sélection
export const useTicketSelection = () => {
  const [selectedTickets, setSelectedTickets] = useState<SupportTicket[]>([]);
  const [selectAll, setSelectAll] = useState(false);

  const toggleTicketSelection = (ticket: SupportTicket) => {
    setSelectedTickets((prev) => {
      const isSelected = prev.some((t) => t.id === ticket.id);
      const newSelection = isSelected
        ? prev.filter((t) => t.id !== ticket.id)
        : [...prev, ticket];
      return newSelection;
    });
  };

  const toggleSelectAll = (allTickets: SupportTicket[]) => {
    if (selectAll) {
      setSelectedTickets([]);
    } else {
      setSelectedTickets(allTickets);
    }
    setSelectAll(!selectAll);
  };

  const clearSelection = () => {
    setSelectedTickets([]);
    setSelectAll(false);
  };

  const isTicketSelected = (ticketId: string) => {
    return selectedTickets.some((t) => t.id === ticketId);
  };

  return {
    selectedTickets,
    selectAll,
    toggleTicketSelection,
    toggleSelectAll,
    clearSelection,
    isTicketSelected,
  };
};
